/**
 * Constantine manifest — built from real metadata at constantine pin
 *   praetorian-inc/constantine@3625e79
 *   (branch: feat/module-metadata, PR #132)
 *
 *   - 3625e79: bench anchors gain `corrected_total_usd` field —
 *     editor-target truth carried separately from `actual_total_usd`
 *     (historical billing). Editor's drift guard now asserts against
 *     the corrected value when present; threshold tightened from 4×
 *     to 2× since corrected values eliminate the pre-fix-bug slack.
 *
 *   - 5113f2b: calibration.observed_p10_p90_ratio (audit follow-up A).
 *     Populated on cwe-fanout per-CWE agentic sweep (2.6) and
 *     deltascan-scanner L1 scan (2.1). Editor's leafBounds() uses it
 *     for log-normal-ish bounds when present; falls back to
 *     confidence-derived linear spread otherwise.
 *   - 5113f2b: dist/bench-anchors.json — sanitized cost observations
 *     from prod runs. Editor's benchAnchors.test.ts asserts projection
 *     is within 3× of measured cost per anchor, surfacing caveats from
 *     the JSON in failure messages.
 *   - dcd73e6: docs/telemetry-feedback-loop.md (audit follow-up D).
 *     Design-only commit; runtime hook + calibrate-refit CLI land in a
 *     separate constantine PR.
 *
 *   - 7872c8a: audit-driven batch (co-designed on agent-bridge thread):
 *     • dist/model-routing.json — per-provider logical→routed SKU table,
 *       opt-in / additive-override / missing-key-passes-bare semantics.
 *       UI's resolveModelId rewrites to single-attempt against the table
 *       (no more suffix probing).
 *     • PerUnitRate gains cache_creation_tokens_per_unit +
 *       cache_read_tokens_per_unit (optional). Pricing reads ci/cr from
 *       dist/model-pricing.json — provider-portable, no Anthropic-cache
 *       hardcoding. cwe-fanout per-agent rate now splits its
 *       cache-folded effective-input number into 4 separate fields.
 *     • scored-functions emits.bytes driver upgraded to multi-knob:
 *       min(target.size_bytes, min(chunk_size, max_batch_files * per_chain_cap)).
 *     • layer2_models normalized to always be a non-empty list at
 *       manifest-gen time; UI drops the strip-trailing-s singular promo.
 *     • cwe-fanout sweep driver uses expr-eval's `if()` for boolean
 *       knob coercion (use_pinned_25 / use_dynamic_25).
 *
 *   - 6cf269f: unified `emits` chain replaces the two prior special-cased
 *     fields `findings_projection` (detect-only) and `output_bytes_projection`
 *     (ingest-only). Any module can declare any number of typed outputs
 *     under `emits.<key>` (driver, rate, confidence); the evaluator walks
 *     the pipeline in stage order and accumulates each module's
 *     contributions into the `input.<key>` namespace for downstream
 *     modules. Ingest emits `bytes` (capped at chunk_size for chunked
 *     ingests); detect/review/skeptic emit `findings_count` at their
 *     respective confirmation rates.
 *
 * Bump history:
 *   67f3477 → ece4640 → b92b147 → 3f5a88d → f7bb2da → 26102ba
 *   - ece4640: added findings_projection on 7 detect modules, int-literal
 *     max_iterations workarounds for the regex-evaluator parser bug.
 *   - b92b147: deltascan-scanner.L2 marked confidence:0 after abbott-prod
 *     over-projection trace (~32%). L2 only fires on L1-hit-positive
 *     units; no `input.l1_hits` namespace yet to express that.
 *   - 3f5a88d: reverted int-literal max_iterations workarounds now that
 *     the expr-eval evaluator supports ceil(...) * N + min() properly.
 *     Remaining workarounds: multi-pass.deep_pass and llm-scanner skeptic
 *     drivers want sibling-component refs (`triage_pass.units`,
 *     `fast_actor.units`) which the evaluator doesn't yet support.
 *   - bf3649a + f7bb2da: LiteLLM-derived dist/model-pricing.json shipped
 *     as a sibling artifact, replacing the buggy hand-rolled MODEL_PRICING
 *     dict that was making Bedrock SKUs fall through to the $3/$15
 *     Sonnet-class default. Real Bedrock-vs-direct premium is ~10%.
 *   - 26102ba: cwe-fanout-scanner per-CWE agentic sweep had silently $0
 *     because expected_iterations was null + model_id was wrong (Haiku
 *     → Sonnet). Now correctly projects ~$14.75 at default config.
 *   - 9ce9eb5: deltascan-scanner L2 promoted from bounded_agentic to
 *     per_unit_ensemble with `models: 'module.layer2_models'` (config-
 *     driven). Editor singular→list promotion mirrors constantine
 *     runtime: empty layer2_models + non-empty layer2_model → use
 *     [layer2_model]. New 'ensemble_unresolved' reason surfaces when
 *     neither list nor singular knob is set.
 *   - 14718b2: sast-scanner findings_projection landed (driver
 *     `min(target.size_bytes/5000, module.max_findings)`, rate 1.0,
 *     confidence 0.4); reviewer cost now scales with repo size via
 *     min(input.findings_count, module.max_findings).
 *   - f262828: deltascan-scanner L2 confidence 0.0 → 0.4 with notes that
 *     the per-token rate is model-independent; driver overcount is a
 *     separate (still-tracked) issue.
 *   - f5d0ea1: new top-level `guard: boolean` field. 15 modules cleared
 *     for the swap/add picker (guard:true); 9 quarantined (guard:false)
 *     because of integration bugs or missing calibration. Custom-uploaded
 *     pipelines that reference guard:false modules still render the row
 *     so user config isn't silently dropped, but the picker won't offer
 *     them as choices.
 *   - 585e3a7: rebound 8 cost_model components from hardcoded model_id
 *     literals to `module.<knob>` driver expressions, so user model swaps
 *     via the editor's per-module config actually update projection.
 *     Covers cwe-fanout-scanner (3 components), deltascan-scanner L1,
 *     deltascan-reviewer (+ -taint), deltascan-exploiter (+ -no-dind).
 *     Side fix in constantine: model_id_override calibration patch no
 *     longer stomps driver expressions.
 *   - f6c7a5f: findings_projection.rate refit from 3.5e-5 → 1.0e-4
 *     (10K bytes/finding) anchored to n=209 paired runs (median 9.7e-5).
 *     Across all 7 LLM detect modules. Effect on the cost panel: reviewer
 *     cap (default 300) now engages on medium-sized targets where it
 *     should — premium-bug-rush at 5MB lands review at ~$208 instead of
 *     the previous $121, matching prod observed runs.
 *
 * - constantine.modules.json was produced by `tools/build_manifest.py`
 *   inside the constantine repo (calibrated cost rates).
 * - The preset JSON files (premium-bug-rush, premium-surgical,
 *   premium-legacy, medium, basic) were converted from
 *   constantine's `configs/<tier>.yaml` at the same commit.
 *
 * This bundle is no longer the editor's primary source: useConstantineManifest
 * fetches GET /constantine/manifest and only falls back here when that request
 * fails or is still in flight. It is therefore deliberately NOT regenerated on
 * every sidecar pin bump — the older module snapshot is what benchAnchors.test.ts
 * measures cost projections against, so refreshing it wholesale would silently
 * rebaseline that drift guard.
 *
 * What must stay in sync is narrower: every preset in CURATED_PRESET_NAMES needs
 * an entry here, and so does every module those presets reference, or the preset
 * silently vanishes in fallback mode. fallbackManifest.test.ts enforces both.
 *
 * agentic-hunter-threat-model and the agentic-hunter module were added from the
 * backend's embedded manifest at constantine pin a1954cbd (3RD-64); every other
 * module stays at its original pin.
 */

import type {
  ModuleManifest,
  PipelineDoc,
  PipelineManifest,
  PresetSummary,
} from '@/sections/attacks/capabilities/components/pipelineEditor/types';

import agenticHunterThreatModelDoc from './agentic-hunter-threat-model.json';
import basicDoc from './basic.json';
import modulesRaw from './constantine.modules.json';
import mediumDoc from './medium.json';
import premiumBugRushDoc from './premium-bug-rush.json';
import premiumLegacyDoc from './premium-legacy.json';
import premiumSurgicalDoc from './premium-surgical.json';

interface RawManifest {
  version: string;
  generated_at: string;
  modules: ModuleManifest[];
}

// TypeScript's JSON import inference is stricter than the schema-validated
// runtime shape (e.g. it picks up the widest union of optional config_knobs
// across all modules). The constantine-side schema validation already
// guarantees the structure, so unknown-casting at the boundary is safe.
const raw = modulesRaw as unknown as RawManifest;

const modulesByName: Record<string, ModuleManifest> = Object.fromEntries(
  raw.modules.map(m => [m.name, m])
);

interface PresetDoc {
  tier?: string;
  cost_indicator?: string;
  notes?: string;
  bench?: { notes?: string };
}

interface PresetSeed {
  /** Canonical preset name (matches the constantine configs/<name>.yaml). */
  name: string;
  /** Human-readable display name for the chip. */
  displayName: string;
  /** Cost indicator string ($ ... $$$$). Not all preset YAMLs declare one. */
  costIndicator: string;
  /** One-line summary shown under the chip. */
  summary: string;
  doc: unknown;
}

function buildPreset(seed: PresetSeed): PresetSummary {
  const doc = seed.doc as PipelineDoc & PresetDoc;
  // Prefer explicit fields from the YAML when present; otherwise fall
  // back to the seed (the YAML header-only tier comments aren't
  // parseable, so we hand-author the labels here).
  return {
    name: doc.tier ?? seed.name,
    display_name: seed.displayName,
    cost_indicator: doc.cost_indicator ?? seed.costIndicator,
    summary: doc.bench?.notes ?? doc.notes ?? seed.summary,
    doc,
  };
}

export const CONSTANTINE_MANIFEST: PipelineManifest = {
  manifest_version: raw.version,
  modules: modulesByName,
  presets: [
    buildPreset({
      name: 'premium-bug-rush',
      displayName: 'Premium Bug Rush',
      costIndicator: '$$$$',
      summary: 'Max recall — Sonnet + GPT-5.5 ensemble at L2.',
      doc: premiumBugRushDoc,
    }),
    buildPreset({
      name: 'premium-surgical',
      displayName: 'Premium Surgical',
      costIndicator: '$$$',
      summary: 'GPT-5.5 solo at L2 for single-shot precision.',
      doc: premiumSurgicalDoc,
    }),
    buildPreset({
      name: 'premium-legacy',
      displayName: 'Premium Legacy',
      costIndicator: '$$$',
      summary: 'Haiku L1 + Opus L2. Thorough scans of large repos.',
      doc: premiumLegacyDoc,
    }),
    buildPreset({
      name: 'medium',
      displayName: 'Medium',
      costIndicator: '$$',
      summary: 'Sonnet solo at L2. Balanced scanner-only run.',
      doc: mediumDoc,
    }),
    buildPreset({
      name: 'basic',
      displayName: 'Basic',
      costIndicator: '$',
      summary: 'Sonnet solo (100k) + Haiku reviewer. Cheapest viable.',
      doc: basicDoc,
    }),
    buildPreset({
      name: 'agentic-hunter-threat-model',
      displayName: 'Agentic Hunter Threat Model',
      costIndicator: '$$$',
      summary:
        'Threat-model-guided autonomous agent; reads code on demand to trace data flows.',
      doc: agenticHunterThreatModelDoc,
    }),
  ],
};
